import React, { FC, useEffect, useMemo } from 'react';
import { createSignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';
import { useInView } from 'react-intersection-observer';

import { PlayerBaseInfoProvider } from '@/scenarios/legacy-player/components/player-manager/context/PlayerBaseInfoProvider';

import { Filmoscope } from '../Filmoscope/Filmoscope';
import { usePlayerConfig } from '../feature-player/hooks/config/use-player-config';
import { usePromoStreamHandler } from '../movie-promo-player/hooks/use-promo-stream-handler';
import { PlayerTracker } from '../feature-player/ui/player-tracker/component';
import { PlayerView } from '../PlayerView/PlayerView';
import { TelemetryContentPlace, TelemetryContentType } from '../Player';
import { PlayerControls } from '../PlayerControls/PlayerControls';
import { PlayerControlButtonSize } from '../PlayerControls/parts/PlayerControlButton.types';
import { usePlayerInstance } from '../../lib/contexts/instance';
import { useInitTracker } from '../feature-player/hooks/tracker/use-init-tracker';
import { getStreamsTrackingData } from '../feature-player/utils/get-streams-tracking-data';
import { usePromoCurrentStream } from '../movie-promo-player/hooks/use-promo-current-stream';
import { usePlayerAnalytics } from '../feature-player/hooks/tracker/use-player-analytics';
import { clearPsid, getPsid } from '../Player/lib/utils/psid';
import { setPlayingParams } from '../feature-player/utils/set-playing-params';
import { playingContentIdVar } from '../player-manager/hooks/use-playing-content-id-var';
import { useOnError } from '../Player/hooks/useOnError';
import { useAddErrorData } from '../error-boundary-context/context';
import { ErrorBondaryContext } from '../error-boundary-context/component';
import { errorMap } from '../error-boundary-context/error-map/map';
import { useActiveTabController } from '../feature-player/hooks/controller/use-active-tab-controller';

import styles from './BackgroundPlayer.module.css';

interface BackgroundPlayerSkinProps {
    contentId: string;
    buttonSize: PlayerControlButtonSize;
    onStart?: VoidFunction;
    onFinish?: VoidFunction;
}

const BackgroundPlayerSkin: FC<BackgroundPlayerSkinProps> = ({
    contentId,
    buttonSize,
    onStart,
    onFinish,
}) => {
    const instance = usePlayerInstance();
    const onError = useOnError({
        contentId,
    });
    const addErrorData = useAddErrorData();

    const controller = useMemo(() => {
        const muted = createSignal(true);

        return {
            muted: muted.seal(),

            get psid() {
                return getPsid();
            },

            init() {
                if (!instance) {
                    return () => {};
                }

                instance.setIsMuted(muted.get());
                instance.setIsPictureInPictureAllowed(false);

                const unsubscribe = [
                    ...(onStart ? [instance.onContentImpression(onStart)] : []),
                    ...(onFinish ? [instance.onFinish(onFinish)] : []),
                    instance.onReady(() => {
                        addErrorData({ vsid: instance.vsid });
                    }),
                    instance.onError(onError),
                ];

                return () => {
                    clearPsid();
                    unsubscribe.forEach((unsubscribe) => unsubscribe());
                };
            },

            play() {
                if (!instance?.isStarted) {
                    instance?.setPlayingContentId(contentId);
                }
                instance?.play();
            },

            pause() {
                instance?.pause();
            },

            toggleMute() {
                muted.set(!muted.get());
                instance?.setIsMuted(muted.get());
            },

            openFullscreen() {
                if (!instance) {
                    return;
                }

                instance.pureInstance.stop();

                clearPsid();
                onFinish?.();

                setPlayingParams({ trailerStartPosition: instance.currentTime });
                playingContentIdVar.set(contentId);
            },

            onInViewChange: (inView: boolean) => {
                if (inView) {
                    controller.play();
                } else {
                    controller.pause();
                }
            },
        };
    }, [contentId, instance, onStart, onFinish, onError, addErrorData]);

    // player tracker

    const currentStream = usePromoCurrentStream(contentId);
    useInitTracker({
        contentId,
        streamTrackingData: getStreamsTrackingData(currentStream?.streamMeta),
    });
    usePlayerAnalytics({
        psid: controller.psid,
        contentId,
        manifestUrl: currentStream?.uri,
    });

    // ---

    const { ref } = useInView({
        onChange: controller.onInViewChange,
        threshold: 0.2,
        skip: !instance,
    });

    useActiveTabController({ play: controller.play, pause: controller.pause });

    useEffect(() => controller.init(), [controller]);

    const muted = useSignal(controller.muted);

    return (
        <div className={styles.root} ref={ref}>
            <PlayerView objectFit="cover">
                <PlayerControls
                    isMuted={muted}
                    onMuteClick={controller.toggleMute}
                    onFullscreenClick={controller.openFullscreen}
                    buttonSize={buttonSize}
                    className={styles.controls}
                />
            </PlayerView>
        </div>
    );
};

interface BackgroundPlayerProps extends BackgroundPlayerSkinProps {
    telemetryContentPlace: TelemetryContentPlace;
    telemetryContentType: TelemetryContentType;
}

const BackgroundPlayerInner: FC<BackgroundPlayerProps> = ({
    contentId,
    telemetryContentPlace,
    telemetryContentType,
    buttonSize,
    onStart,
    onFinish,
}) => {
    const { config, playerBaseInfoContent } = usePlayerConfig({
        streamHandler: usePromoStreamHandler({
            telemetryContentPlace,
            telemetryContentType,
        }),
    });

    return (
        <Filmoscope config={config} useYasp>
            <PlayerTracker>
                <PlayerBaseInfoProvider value={playerBaseInfoContent}>
                    <BackgroundPlayerSkin
                        contentId={contentId}
                        buttonSize={buttonSize}
                        onStart={onStart}
                        onFinish={onFinish}
                    />
                </PlayerBaseInfoProvider>
            </PlayerTracker>
        </Filmoscope>
    );
};

export const BackgroundPlayer: FC<BackgroundPlayerProps> = (props) => (
    <ErrorBondaryContext errorMap={errorMap}>
        <BackgroundPlayerInner {...props} />
    </ErrorBondaryContext>
);
